import DeleteIcon from '@mui/icons-material/Delete';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import { Box, IconButton, SxProps, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { Accept, DropzoneProps, useDropzone } from 'react-dropzone';

export interface DropzoneFieldProps {
  containerSx?: SxProps;
  onDrop: (acceptedFiles: File[]) => void;
  files: File[];
  emptyContent?: React.ReactNode;
  iconWrapperStyle?: React.CSSProperties;
  maxFiles: number;
  onClear?: () => void;
  accept?: Accept;
  disabled?: boolean;
  maxSize?: number;
  readOnly?: boolean;
}

export type ReactDropzoneProps = DropzoneProps;

export const DropzoneField: React.FC<DropzoneFieldProps> = ({
  containerSx,
  onDrop,
  onClear,
  files = [],
  maxFiles = 1,
  emptyContent,
  iconWrapperStyle,
  accept = {
    'image/jpeg': [],
    'image/png': [],
    'image/gif': [],
    'image/webp': [],
  },
  disabled = false,
  maxSize = 10 * 1024 * 1024,
  readOnly = false,
}) => {
  const { getRootProps, getInputProps, isDragReject } = useDropzone({
    maxSize: maxSize,
    onDrop,
    maxFiles,
    accept: accept,
  });

  const filePreviews = useMemo(
    () => (files || [])?.filter(file => !!file).map(file => URL.createObjectURL(file)),
    [files],
  );

  const isDisabled = disabled || readOnly;

  return (
    <Box
      {...getRootProps()}
      sx={{
        ...containerSx,
      }}
      position={'relative'}>
      <input disabled={isDisabled} {...getInputProps()} />
      {filePreviews.length > 0 ? (
        <div className='flex flex-col items-center gap-y-6 relative h-[100%]'>
          {filePreviews.map((preview, idx) => (
            <div
              key={idx}
              style={{
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                height: '100%',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                border: '2px solid transparent',
                borderRadius: '8px',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLDivElement).style.transform = 'scale(1.05)';
                (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.3)';
                (e.currentTarget as HTMLDivElement).style.border = '2px solid white';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLDivElement).style.transform = 'scale(1)';
                (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                (e.currentTarget as HTMLDivElement).style.border = '2px solid transparent';
              }}>
              <img
                key={idx}
                src={preview}
                alt={`Preview ${idx}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
              />
            </div>
          ))}
        </div>
      ) : (
        emptyContent || (
          <Typography
            color='white'
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
              alignItems: 'center',
              fontSize: '14px',
            }}>
            <PhotoOutlinedIcon
              sx={{
                color: 'white',
              }}
            />{' '}
            {isDragReject ? 'Rejected! Please try again' : 'Drag & drop file here, or Select from Device'}
          </Typography>
        )
      )}
      {filePreviews.length > 0 && !isDisabled ? (
        <div className='absolute top-4 right-4' style={iconWrapperStyle}>
          <IconButton
            disabled={isDisabled}
            aria-label='delete'
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              onClear && onClear();
            }}>
            <DeleteIcon
              className='cursor-pointer'
              sx={{
                color: '#F04923',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
              }}
            />
          </IconButton>
        </div>
      ) : null}
    </Box>
  );
};
